import { first } from 'lodash';
import { useEffect, useMemo } from 'react';
import { getProductImages } from 'src/components/widget/lineupWidget/editProductPanel/EditProductPanel.helper';
import { DYRecommendationsStorage } from 'src/constants';
import { useModal } from 'src/hooks';
import { IProduct } from 'src/interfaces';

import { VariationMap } from './VariationTemplateMappingStep.utils';

interface UseVariationTemplateMappingStepProps {
  variationMap: VariationMap;
  setVariationMap: React.Dispatch<React.SetStateAction<VariationMap>>;
  template?: { number_of_variations?: number };
}

export const useVariationTemplateMappingStep = ({
  variationMap,
  setVariationMap,
  template,
}: UseVariationTemplateMappingStepProps) => {
  const productListModal = useModal();

  const mainProductCode = variationMap[1]?.productCode || '';

  const filters = useMemo(() => {
    const usedProductCodes = Object.entries(variationMap)
      .map(([, v]) => v.productCode)
      .filter(Boolean);

    if (usedProductCodes.length === 0) return [];

    return [
      `product_code.notlist=${encodeURIComponent(usedProductCodes.join('|'))}`,
    ];
  }, [variationMap]);

  const showPreviewPlaceholder = !mainProductCode;

  const isSingleProduct = useMemo(
    () => template?.number_of_variations === 1,
    [template?.number_of_variations],
  );

  useEffect(() => {
    const selectedVariationCode = variationMap[1]?.variationCode;
    if (!selectedVariationCode) return;
    localStorage.setItem(
      DYRecommendationsStorage,
      JSON.stringify({ items: [{ code: selectedVariationCode }] }),
    );

    return () => {
      localStorage.removeItem(DYRecommendationsStorage);
    };
  }, [variationMap]);

  const handleSelectProduct = (products: IProduct[], index: number) => {
    const product = first(products);
    productListModal.onCloseModal();
    setVariationMap((prev) => {
      const updated: VariationMap = {
        ...prev,
        [index]: {
          productCode: product?.product_code || '',
          variationCode: product?.productVariations?.[0].product_code || '',
          assetName: first(getProductImages(product))?.name || '',
        },
      };

      return updated;
    });
  };

  const handleVariationChange = (index: number, value: string) => {
    setVariationMap((prev) => {
      const updated: VariationMap = {
        ...prev,
        [index]: {
          ...prev[index],
          variationCode: value,
        },
      };

      return updated;
    });
  };

  const handleProductCodeChange = (index: number, value: string) => {
    setVariationMap((prev) => {
      const updated: VariationMap = {
        ...prev,
        [index]: {
          ...prev[index],
          productCode: value,
        },
      };

      return updated;
    });
  };

  const handleShowFlavorsChange = (index: number, value: boolean) => {
    setVariationMap((prev) => {
      const updated: VariationMap = {
        ...prev,
        [index]: {
          ...prev[index],
          showFlavors: value,
        },
      };

      return updated;
    });
  };

  return {
    productListModal,
    handleSelectProduct,
    handleVariationChange,
    handleProductCodeChange,
    handleShowFlavorsChange,
    filters,
    showPreviewPlaceholder,
    isSingleProduct,
  };
};
